import Vue from 'vue'

// Shared parse/serialize helpers for the legacy "*,-value" tenant filter
// string format. These are pure functions so the UI can round-trip the
// exact strings the backend already understands, without changing any
// backend matching logic.
//
// Wildcard format (used by most `*_filter` fields):
//   ''                -> allow everything
//   '*'                -> allow everything
//   '*,-A,-B'          -> allow everything except A and B
//   'A,B'              -> only allow A and B (no `*` present)
// Anything that mixes bare inclusions with a wildcard, or exclusions
// without a wildcard, is treated as ambiguous/raw so we never silently
// misrepresent an existing value.
//
// Plain-list format (used by simple "skip these" fields whose backend
// check is a plain substring/membership test, not `*`/`-` aware):
//   ''                -> nothing skipped
//   'A,B'              -> A and B are skipped
//
// A value that itself contains a comma (e.g. a base name like
// "GREENVILLE, PA GARAGE") is wrapped in double quotes on serialize so it
// round-trips as one token instead of being split apart — e.g. 'A,"B, C"'
// parses to values ['A', 'B, C']. Unquoted strings (all existing stored
// values) parse exactly as before, since there are no quotes to toggle
// quoted-span tracking on.

// splits a comma-delimited string into raw tokens, but ignores commas that
// fall inside a double-quoted span so a single value can contain a literal
// comma (e.g. a base name like "GREENVILLE, PA GARAGE"). a doubled quote
// ("") inside a quoted span is kept as-is here (unescaped by unquoteToken)
// so it survives the split untouched. strings with no quote characters at
// all split identically to a plain str.split(',') — this is what keeps
// existing unquoted stored values parsing the same as before.
function splitRespectingQuotes(str) {
  const tokens = []
  let current = ''
  let insideQuotes = false
  for (let i = 0; i < str.length; i++) {
    const ch = str[i]
    if (ch === '"') {
      if (insideQuotes && str[i + 1] === '"') {
        current += '""'
        i++
        continue
      }
      insideQuotes = !insideQuotes
      current += ch
      continue
    }
    if (ch === ',' && !insideQuotes) {
      tokens.push(current)
      current = ''
      continue
    }
    current += ch
  }
  tokens.push(current)
  return tokens
}

// strips a token's surrounding quotes (if any) and unescapes doubled
// internal quotes. tokens with no surrounding quotes pass through as-is.
function unquoteToken(rawToken) {
  const t = rawToken.trim()
  if (t.length >= 2 && t[0] === '"' && t[t.length - 1] === '"') {
    return t.slice(1, -1).replace(/""/g, '"')
  }
  return t
}

// wraps a value in quotes (escaping any literal quote) only when needed —
// i.e. it contains a comma or a quote — so plain values keep serializing
// exactly as before.
function quoteIfNeeded(value) {
  if (value.includes(',') || value.includes('"')) {
    return '"' + value.replace(/"/g, '""') + '"'
  }
  return value
}

export function parseFilter(str) {
  const trimmed = (str || '').trim()
  if (trimmed === '') {
    return { mode: 'exclude', values: [], raw: null }
  }

  const rawTokens = splitRespectingQuotes(trimmed).map(t => t.trim())
  for (const t of rawTokens) {
    if (t === '' || t === '-') {
      return { mode: null, values: [], raw: str }
    }
  }

  const hasWildcard = rawTokens.some(t => t === '*')
  const exclusionTokens = rawTokens.filter(t => t !== '*' && t.startsWith('-'))
  const inclusionTokens = rawTokens.filter(t => t !== '*' && !t.startsWith('-'))
  const exclusions = exclusionTokens.map(t => unquoteToken(t.slice(1)))
  const inclusions = inclusionTokens.map(t => unquoteToken(t))

  if (hasWildcard && inclusions.length === 0) {
    return { mode: 'exclude', values: exclusions, raw: null }
  }
  if (!hasWildcard && exclusions.length === 0) {
    return { mode: 'allow', values: inclusions, raw: null }
  }
  // mixed *,A or bare-exclusion-only without * — ambiguous, don't guess
  return { mode: null, values: [], raw: str }
}

export function serializeFilter({ mode, values }) {
  const clean = (values || []).map(v => v.trim()).filter(v => v !== '')
  if (mode === 'allow') {
    return clean.map(quoteIfNeeded).join(',')
  }
  // 'exclude' mode (default): empty means allow-all
  return clean.length ? '*,' + clean.map(v => '-' + quoteIfNeeded(v)).join(',') : ''
}

export function parseList(str) {
  const trimmed = (str || '').trim()
  if (trimmed === '') return []
  return splitRespectingQuotes(trimmed).map(t => unquoteToken(t)).filter(t => t !== '')
}

export function serializeList(values) {
  return (values || []).map(v => v.trim()).filter(v => v !== '').map(quoteIfNeeded).join(',')
}

// Several backend consumers read filter fields via a plain `.get()` with no
// schema default, so the key can be entirely absent from a loaded model or
// modal-scoped object for tenants/records that never set it. Assigning a
// brand-new key via v-model's plain assignment doesn't make it reactive in
// Vue 2, which can silently break dirty-tracking (main page) or leave stale
// chip displays (modals). Call this right after the target object is
// populated, before any FilterSettingEditor bound to these keys can write
// to it.
export function ensureFilterKeys(target, keys) {
  keys.forEach(k => {
    if (!(k in target)) Vue.set(target, k, '')
  })
}
